import { Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import React, { FC, MouseEventHandler, useState } from 'react';
import Ripples from 'react-ripples';
import * as yup from 'yup';
import { UserManagementType } from '../../../models/user-management-type';
import subaccountCreate from '../../../services/customer-api/endpoints/subaccount/create';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import FormText from '../../atoms/form-text/form-text';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import usePushFormikFormToDataLayer from '../../../hooks/use-push-formik-form-to-data-layer/use-push-formik-form-to-data-layer';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

interface Props extends UserManagementType {
  readonly onCancel?: MouseEventHandler<HTMLButtonElement>;
}

const AddNewUserForm: FC<Props> = ({
  backToUsers,
  firstNameLabel,
  firstNamePlaceholder,
  firstNameRequiredValidationMessage,
  lastNameLabel,
  lastNamePlaceholder,
  lastNameRequiredValidationMessage,
  emailAddressLabel,
  emailAddressNotInAValidFormat,
  emailAddressPlaceholder,
  emailAddressRequiredValidationMessage,
  emailNotUniqueDescription,
  failedToSendInviteDescription,
  inviteLandingPage,
  inviteSentDescription,
  inviteSentHeading,
  inviteSentReturnToUsers,
  inviteUsersDescription,
  inviteUsersHeading,
  onCancel,
  sendInvite,
}: Props) => {
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const formContext = usePushFormikFormToDataLayer('Add user', showSuccess);

  return (
    <div className="mx-auto w-full">
      {onCancel && (
        <div className="flex justify-center mb-2 md:mb-4 lg:mb-6">
          <Ripples
            className="rounded-full"
            color={`rgba(${frenchBlue.rgb}, .2)`}
            during={1200}
          >
            <button
              className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
              onClick={onCancel}
              type="button"
            >
              <IcomoonIcon
                className="-mb-2px"
                color="grey-600"
                name="chevron-left"
                size="md"
              />

              <span className="mx-4">{backToUsers}</span>
            </button>
          </Ripples>
        </div>
      )}

      <div className="text-center md:w-1/2 xl:w-1/3 mx-auto max-w-md">
        {!showSuccess && (
          <>
            <h4 className="page-heading-four text-center mb-6">
              {inviteUsersHeading}
            </h4>
            <p className="mb-8 md:mb-10 lg:mb-12 text-grey-600 px-4">
              {inviteUsersDescription}
            </p>
            <Formik
              innerRef={formContext}
              onSubmit={async (values, { setErrors }) => {
                try {
                  await subaccountCreate(
                    values.email,
                    values.firstName,
                    values.lastName,
                    inviteLandingPage.url
                  );
                  setShowSuccess(true);
                } catch (error) {
                  if (error?.response?.status === 400) {
                    setErrors({
                      email: emailNotUniqueDescription,
                    });

                    return;
                  }
                  setErrors({
                    email: failedToSendInviteDescription,
                  });
                }
              }}
              initialValues={{ email: '', firstName: '', lastName: '' }}
              validateOnMount
              validateOnChange
              validationSchema={yup.object({
                firstName: yup
                  .string()
                  .required(firstNameRequiredValidationMessage),
                lastName: yup
                  .string()
                  .required(lastNameRequiredValidationMessage),
                email: yup
                  .string()
                  .email(emailAddressNotInAValidFormat)
                  .required(emailAddressRequiredValidationMessage),
              })}
            >
              <Form className="text-left flex flex-col" noValidate>
                <div className="w-full mb-4 md:flex md:gap-4">
                  <div className="md:flex-1">
                    <FormText
                      name="firstName"
                      label={firstNameLabel}
                      placeholder={firstNamePlaceholder}
                      required
                    />
                  </div>
                  <div className="md:flex-1">
                    <FormText
                      name="lastName"
                      label={lastNameLabel}
                      placeholder={lastNamePlaceholder}
                      required
                    />
                  </div>
                </div>
                <div className="w-full mb-4">
                  <FormText
                    name="email"
                    type="email"
                    placeholder={emailAddressPlaceholder}
                    label={emailAddressLabel}
                    required
                  />
                </div>

                <div className="flex justify-center my-component-spacing-mobile lg:my-component-spacing-content">
                  <ButtonPrimary buttonType="submit" name={sendInvite} />
                </div>
              </Form>
            </Formik>
          </>
        )}
        {showSuccess && (
          <>
            <motion.div
              animate={{ scale: 1 }}
              className="flex flex-col items-center justify-center my-4"
              initial={{ scale: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h4 className="page-heading-three text-center mb-8 max-w-xl">
                {inviteSentHeading}
              </h4>
              <IcomoonIcon
                name="check"
                className="p-4 text-6xl rounded-full bg-turquoise-light text-white"
              />
            </motion.div>

            <p className="pb-8 max-w-md mx-auto pt-4 text-center text-grey-600">
              {inviteSentDescription}
            </p>

            <div className="flex justify-center">
              <ButtonPrimary
                name={inviteSentReturnToUsers}
                onClick={onCancel}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

AddNewUserForm.defaultProps = {
  onCancel: null,
};

export default AddNewUserForm;
